import React from 'react';
import { FaBolt, FaRegCalendarAlt } from 'react-icons/fa';
import StudyStreakIcon from '../Achievements/StudyStreakIcon';
import MasteryBrainIcon from '../Achievements/MasteryBrainIcon';
import { SNIPPET_MASTERY_LABEL } from '../../constants/snippetMasteryCopy';
import { formatDayStreakLabel } from '../../utils/dayStreak';

const SRS_RATING_LABELS = {
  easy: 'Easy recall',
  good: 'Good recall',
  hard: 'Hard recall',
  again: 'Needs practice'
};

/**
 * Post-rep modal — SRS recall feedback and optional session summary tab.
 */
export default function StudyExperiencePopup({
  isOpen,
  panel = 'rep',
  onPanelChange,
  experienceData,
  sessionSummary = null,
  studyMode,
  displayStreak = 0,
  streakAtRisk = false,
  completedToday = 0,
  masteredToday = 0,
  showSessionTab = false,
  showContinuePath = false,
  continuePathLabel = 'Continue path',
  onContinuePath,
  onDismiss,
  dismissLabel = 'Great!'
}) {
  if (!isOpen) return null;

  const isLearnComplete = experienceData?.accuracy !== undefined;
  const correctCount = sessionSummary?.correctCount ?? 0;
  const totalAttempted = sessionSummary?.totalAttempted ?? 0;
  const canShowSessionTab = showSessionTab && correctCount > 0 && !isLearnComplete;
  const activePanel = canShowSessionTab && panel === 'session' ? 'session' : 'rep';

  const srsRating = studyMode === 'regular' ? experienceData?.srsRating : null;
  const srsRatingLabel = srsRating ? SRS_RATING_LABELS[srsRating] || srsRating : '';
  const srsDueLabel = studyMode === 'regular' ? (experienceData?.srsScheduleLabel || '') : '';
  const newlyMastered = Boolean(experienceData?.newlyMastered);
  const showSrsRepPanel = Boolean(srsRatingLabel) || newlyMastered;
  const useSrsOnlyLayout = activePanel === 'rep' && showSrsRepPanel && !isLearnComplete;

  const accuracyPct = isLearnComplete
    ? Math.max(0, Math.min(100, Number(experienceData.accuracy) || 0))
    : 0;

  const streakLabel = formatDayStreakLabel(displayStreak);
  const sessionHeadline =
    correctCount === totalAttempted && totalAttempted > 0
      ? 'Perfect run'
      : 'Today so far';

  return (
    <div className="study__experience-popup">
      <div
        className={`study__experience-content${
          activePanel === 'session' ? ' study__experience-content--with-roles' : ''
        }${useSrsOnlyLayout ? ' study__experience-content--srs-only' : ''}`}
      >
        <div className="study__experience-content__icon" aria-hidden="true">
          {activePanel === 'session' ? (
            <StudyStreakIcon
              className="study__experience-content__streak-glyph"
              active={displayStreak > 0}
            />
          ) : (
            <FaBolt />
          )}
        </div>

        {canShowSessionTab ? (
          <div
            className="study__experience-content__panel-nav"
            role="tablist"
            aria-label="Study summary views"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activePanel === 'rep'}
              className={`study__experience-content__panel-tab${
                activePanel === 'rep' ? ' study__experience-content__panel-tab--active' : ''
              }`}
              onClick={() => onPanelChange?.('rep')}
            >
              Stat
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activePanel === 'session'}
              className={`study__experience-content__panel-tab${
                activePanel === 'session' ? ' study__experience-content__panel-tab--active' : ''
              }`}
              onClick={() => onPanelChange?.('session')}
            >
              Today
            </button>
          </div>
        ) : null}

        {activePanel === 'session' ? (
          <>
            <div className="study__experience-content__header">
              <p className="study__experience-content__label">{sessionHeadline}</p>
              <ul className="study__experience-content__session-stats" aria-label="Today's progress">
                <li className="study__experience-content__session-stat">
                  <span className="study__experience-content__session-stat-icon" aria-hidden>
                    <FaRegCalendarAlt />
                  </span>
                  <span className="study__experience-content__session-stat-text">
                    <strong className="study__experience-content__session-stat-value">
                      {completedToday}
                    </strong>{' '}
                    {completedToday === 1 ? 'new snippet' : 'new snippets'} started
                  </span>
                </li>
                <li className="study__experience-content__session-stat">
                  <span className="study__experience-content__session-stat-icon" aria-hidden>
                    <MasteryBrainIcon className="study__experience-content__session-stat-mastery-icon" />
                  </span>
                  <span className="study__experience-content__session-stat-text">
                    <strong className="study__experience-content__session-stat-value">
                      {masteredToday}
                    </strong>{' '}
                    {masteredToday === 1 ? 'snippet' : 'snippets'} mastered
                  </span>
                </li>
              </ul>
            </div>

            <div
              className={`study__experience-content__streak-row${
                displayStreak > 0 ? ' study__experience-content__streak-row--active' : ''
              }${streakAtRisk ? ' study__experience-content__streak-row--at-risk' : ''}`}
            >
              <span className="study__experience-content__streak-count">{streakLabel}</span>
              <span className="study__experience-content__streak-message">Study Streak</span>
            </div>
          </>
        ) : isLearnComplete ? (
          <>
            <div className="study__experience-content__header">
              <p className="study__experience-content__label">Learn Mode Complete</p>
              <div className="study__experience-content__amount">
                <span className="study__experience-content__value">{experienceData.accuracy}</span>
                <span className="study__experience-content__unit">% ACCURACY</span>
              </div>
            </div>

            <div className="study__experience-content__progress">
              <div className="study__experience-content__progress-header">
                <span className="study__experience-content__progress-label">Lines completed</span>
                <span className="study__experience-content__progress-value">
                  {experienceData.correctLines} / {experienceData.totalLines}
                </span>
              </div>
              <div className="study__experience-content__progress-track">
                <div
                  className="study__experience-content__progress-fill"
                  style={{ width: `${accuracyPct}%` }}
                />
              </div>
            </div>
          </>
        ) : null}

        {activePanel === 'rep' && showSrsRepPanel ? (
          <div className="study__experience-content__srs-body">
            <p className="study__experience-content__label">
              {newlyMastered ? SNIPPET_MASTERY_LABEL : 'Recall rating'}
            </p>
            <div
              className="study__experience-content__srs-feedback"
              aria-label={newlyMastered ? SNIPPET_MASTERY_LABEL : 'Recall rating'}
            >
              <span
                className={`study__experience-content__srs-feedback-badge${
                  newlyMastered
                    ? ' study__experience-content__srs-feedback-badge--mastered'
                    : ` study__experience-content__srs-feedback-badge--${srsRating}`
                }`}
              >
                {newlyMastered ? SNIPPET_MASTERY_LABEL : srsRatingLabel}
              </span>
              {srsDueLabel ? (
                <span className="study__experience-content__srs-feedback-interval">
                  {srsDueLabel}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        {activePanel === 'session' && showContinuePath ? (
          <button
            type="button"
            className="study__experience-content__secondary-action"
            onClick={onContinuePath}
          >
            {continuePathLabel}
          </button>
        ) : null}

        <button
          onClick={onDismiss}
          className="study__experience-content__continue"
          type="button"
        >
          {dismissLabel}
        </button>
      </div>
    </div>
  );
}
